// cart.js
import { qs, qsAll } from '../utils/domHelpers.js';
import { stateManager } from '../utils/stateManager.js';
import { trapFocus, releaseFocus } from '../utils/focusManager.js';

export class Cart {
	constructor(baseQueryEl) {
		this.baseQueryEl = baseQueryEl || document;
		this.cartDrawer = qs(".cart-drawer");
		this.isCartPage = !!qs(".cart-page");
		this._qtyDelegatedBound = false;
		this._eventsBound = false; // NEW
		this.bindQuantityButtons();
		this.bindBasicUI();
		this.bindEvents();
	}

	bindBasicUI() {
		const closeTriggers = qsAll(
			".cart-drawer__close, .cart-drawer__overlay, .drawer__close",
			this.cartDrawer
		);
		closeTriggers.forEach((el) => {
			el.addEventListener("click", () => {
				releaseFocus();
				this.closeCartDrawer();
			});
		});
	}

	addToCart(form) {
		const formDataObj = Object.fromEntries(new FormData(form).entries());
		fetch("/cart/add.js", {
			method: "POST",
			body: JSON.stringify(formDataObj),
			headers: { "Content-Type": "application/json" },
		})
			.then((response) => response.json())
			.then(() => this.refreshCartDrawer())
			.catch((err) => {
				console.error("Error adding to cart", err);
			});
	}

	closeCartDrawer() {
		if (!this.cartDrawer) return;
		stateManager.setState(null);
		document.body.classList.remove("cart--previewed");
		const cartBtn = document.querySelector('.header__cart');
		if (cartBtn) cartBtn.setAttribute('aria-expanded', 'false');
	}

	refreshCart() {
		if (this.isCartPage) {
			this.refreshCartPage();
		} else {
			this.refreshCartDrawer();
		}
	}

	bindCartPageNote() {
		const cartNote = qs("#Cart-note");
		if (!cartNote || cartNote._noteBound) return;
		cartNote._noteBound = true;
		cartNote.addEventListener("change", () => {
			fetch("/cart/update.js", {
				method: "POST",
				headers: { "Content-Type": "application/json", Accept: "application/json" },
				body: JSON.stringify({ note: cartNote.value }),
			})
				.then((response) => response.json())
				.catch((error) => console.error("Error updating cart note:", error));
		});

		const checkoutButton = qs(".cart__checkout-button");
		if (checkoutButton && !checkoutButton._checkoutNoteBound) {
			checkoutButton._checkoutNoteBound = true;
			checkoutButton.addEventListener("click", (e) => {
				if (!cartNote.value.trim()) return;
				e.preventDefault();
				fetch("/cart/update.js", {
					method: "POST",
					headers: { "Content-Type": "application/json", Accept: "application/json" },
					body: JSON.stringify({ note: cartNote.value }),
				})
					.then((response) => response.json())
					.then(() => checkoutButton.form.submit())
					.catch(() => checkoutButton.form.submit());
			});
		}
	}

	refreshCartPage() {
		fetch(window.location.href)
			.then((response) => response.text())
			.then((html) => {
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, "text/html");
				const newContent = doc.querySelector(".cart-page");
				const currentContent = qs(".cart-page");
				if (newContent && currentContent) {
					currentContent.innerHTML = newContent.innerHTML;
				}
				// Update header cart count
				const newHeaderCount = doc.querySelector(".header__cart-count");
				const headerCount = qs(".header__cart-count");
				if (newHeaderCount && headerCount) {
					headerCount.textContent = newHeaderCount.textContent;
				}
				this.bindCartPageNote();
			})
			.catch((err) => console.error("Error refreshing cart page", err));
	}

	refreshCartDrawer() {
		if (!this.cartDrawer) return;
		fetch("/cart?view=drawer")
			.then((response) => response.text())
			.then((html) => {
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, "text/html");
				const newCartContent = doc.querySelector(".cart-drawer");
				if (newCartContent) {
					this.cartDrawer.innerHTML = newCartContent.innerHTML;
					// Update header cart status.
					const headerCart = qs(".header__cart");
					if (headerCart && headerCart.classList.contains("empty")) {
						headerCart.classList.remove("empty");
					}
					// Update cart count (closed state icon in the header).
					const newCartCount =
						doc.querySelector(".header__cart-count");
					const cartCountEl = qs(".header__cart-count");
					if (newCartCount && cartCountEl) {
						cartCountEl.textContent = newCartCount.textContent;
					}
				}
				// Update state for the cart globally.
				document.body.classList.add("cart--previewed");
				stateManager.setState("cart");
				// const focusRootEls = [this.cartDrawer, document.querySelector('.section-header')].filter(Boolean);
				const focusRootEls = [
					document.querySelector("header.page__header"),
					document.querySelector(".drawer__contents"),
				];
				trapFocus(focusRootEls);

				// Check for quantity inputs exceeding max and decrement if necessary
				const cartItems = qsAll(".cart-item", this.cartDrawer);
				cartItems.forEach((item) => {
					const quantityInput =
						item.querySelector(".quantity__input");
					const decrementButton = item.querySelector(
						".quantity-decrement"
					);
					if (quantityInput && decrementButton) {
						const maxAttr = quantityInput.getAttribute("max");
						const max = parseInt(maxAttr, 10);
						const quantity = parseInt(quantityInput.value, 10);
						if (!isNaN(max) && quantity > max) {
							decrementButton.click();
						}
					}
				});

				const closeTriggers = qsAll(
					".cart-drawer__close, .cart-drawer__overlay, .drawer__close",
					this.cartDrawer
				);
				closeTriggers.forEach((el) => {
					el.addEventListener("click", () => {
						releaseFocus();
						this.closeCartDrawer();
					});
				});

				this.cartDrawer.addEventListener("click", (e) => {
					const continueShoppingLink = e.target.closest(
						".cart__continue-shopping"
					);
					if (continueShoppingLink) {
						e.preventDefault();

						releaseFocus();
					}
				});

				const cartNote = qs("#Cart-note", this.cartDrawer);
				if (cartNote) {
					cartNote.addEventListener("change", () => {
						fetch("/cart/update.js", {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Accept: "application/json",
							},
							body: JSON.stringify({
								note: cartNote.value,
							}),
						})
							.then((response) => response.json())
							.then(() => {})
							.catch((error) => {
								console.error(
									"Error updating cart note:",
									error
								);
							});
					});
				}

				const checkoutButton = qs(
					"#CartDrawer-Checkout",
					this.cartDrawer
				);
				if (checkoutButton) {
					checkoutButton.addEventListener("click", (e) => {
						if (cartNote && cartNote.value.trim() !== "") {
							e.preventDefault();
							fetch("/cart/update.js", {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
									Accept: "application/json",
								},
								body: JSON.stringify({
									note: cartNote.value,
								}),
							})
								.then((response) => response.json())
								.then(() => {
									checkoutButton.form.submit();
								})
								.catch((error) => {
									console.error(
										"Error updating cart note before checkout:",
										error
									);
									checkoutButton.form.submit();
								});
						}
					});
				}
			})
			.catch((err) => console.error("Error refreshing cart drawer", err));
	}

	bindEvents() {
		if (this._eventsBound) return;
		this._eventsBound = true;
		const root = this.baseQueryEl || document;

		// Bind cart page note auto-save on initial load
		if (this.isCartPage) {
			this.bindCartPageNote();
		}

		// Handle normal add to cart buttons
		const addToCartButtons = qsAll(".product-form__submit", root);
		addToCartButtons.forEach((button) => {
			button.addEventListener("click", (e) => {
				e.preventDefault();
				const form = button.closest("form");
				this.addToCart(form);
				button.textContent = "Adding to cart...";
				setTimeout(() => {
					button.textContent = "Add to cart";
				}, 3000);
			});
		});

		// Handle the variant dropdown form with pseudo-select
		const variantDropdownForm = qs(
			"#product-form-main-product-variant-dropdown",
			root
		);
		if (variantDropdownForm) {
			const pseudoSelectLinks = qsAll(
				".pseudo__select a",
				variantDropdownForm
			);
			const hiddenVariantInput = qs(
				'input[name="id"]',
				variantDropdownForm
			);

			pseudoSelectLinks.forEach((link) => {
				link.addEventListener("click", (e) => {
					e.preventDefault();
					const variantId = link.dataset.variantId;
					if (variantId && hiddenVariantInput) {
						hiddenVariantInput.value = variantId;

						// Update label text and close dropdown
						const toggleLabel = qs(
							".select__toggle",
							variantDropdownForm
						);
						const pseudoSelect = qs(
							".pseudo__select",
							variantDropdownForm
						);
						const pseudoSelectWrap = qs(
							".select__wrap--pseudo",
							variantDropdownForm
						);
						if (toggleLabel && pseudoSelect && pseudoSelectWrap) {
							toggleLabel.textContent = link.textContent;
							pseudoSelectWrap.classList.remove("options--open");
							toggleLabel.setAttribute("aria-expanded", "false");
						}

						// Add to cart and refresh drawer (not full page redirect)
						// this.addToCart(variantDropdownForm);
					}
				});
			});
		}

		// Handle reveal of variant buttons
		const launchVariantButtons = qsAll(".launch-variants", root);
		launchVariantButtons.forEach((button) => {
			button.addEventListener("click", () => {
				const revealWrap = document.querySelector(
					".variants__reveal-wrap"
				);
				if (revealWrap) {
					revealWrap.classList.add("variants__reveal-wrap--active");
				}
				button.remove();
			});
		});

		// PDP product form: intercept add-to-cart and handle via AJAX
		const pdpForms = root.querySelectorAll(".product-form form");
		pdpForms.forEach((form) => {
			form.addEventListener("submit", (evt) => {
				if (evt.defaultPrevented) return;
				evt.preventDefault();
				this.addToCart(form);
			});
		});

		const pdpButtons = root.querySelectorAll(".product-form__submit");
		pdpButtons.forEach((btn) => {
			btn.addEventListener("click", (evt) => {
				const form = btn.form || btn.closest("form");
				if (!form) return;
				// Avoid double-handling if submit handler already intercepted
				if (evt.defaultPrevented) return;
				evt.preventDefault();
				this.addToCart(form);
			});
		});

		this.bindQuantityButtons();
	}

	bindQuantityButtons() {
		// Delegate quantity button clicks and input changes so it works for both
		// cart drawer (dynamic HTML) and cart page (static) without rebinding.
		if (this._qtyDelegatedBound) return;
		this._qtyDelegatedBound = true;

		const root = this.baseQueryEl || document;

		// Click delegation for + / - buttons
		root.addEventListener("click", (e) => {
			const inc = e.target.closest(
				".quantity__button.quantity-increment"
			);
			const dec = e.target.closest(
				".quantity__button.quantity-decrement"
			);
			if (!inc && !dec) return;

			const btn = inc || dec;
			const qtyWrap = btn.closest(".quantity");
			if (!qtyWrap) return;
			const input = qtyWrap.querySelector(".quantity__input");
			if (!input) return;

			const delta = inc ? 1 : -1;
			const maxAttr = input.getAttribute("max") || btn.dataset.max;
			const max = maxAttr ? parseInt(maxAttr, 10) : NaN;

			// If decrementing and current value is above max due to prior state,
			// normalize so the first click brings it down cleanly.
			if (dec && !isNaN(max)) {
				const current = parseInt(input.value || "0", 10);
				if (current > max) {
					input.value = String(max + 1);
				}
			}

			this.changeCartItemQuantity(btn, delta);
		});

		// Auto-submit/update when typing quantities directly
		root.addEventListener("change", (e) => {
			const target = e.target;
			if (!target.matches(".quantity__input")) return;
			const form = target.closest("form");
			if (form) {
				const formData = new FormData(form);
				const bodyData = new URLSearchParams(formData);
				fetch("/cart/update.js", {
					method: "POST",
					body: bodyData,
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
				})
					.then((response) => response.json())
					.then(() => this.refreshCart())
					.catch((err) =>
						console.error(
							"Error updating quantity via input change:",
							err
						)
					);
			}
		});
	}

	changeCartItemQuantity(button, delta) {
		const input = button.parentElement.querySelector(".quantity__input");
		const form = button.closest("form");
		const maxAttr = input.getAttribute("max") || button.dataset.max;
		const max = maxAttr ? parseInt(maxAttr, 10) : NaN;

		const qtyWrap = button.closest(".quantity");
		const warning = qtyWrap
			? qtyWrap.querySelector(".quantity__warning")
			: null;
		const incBtn = qtyWrap
			? qtyWrap.querySelector(".quantity__button.quantity-increment")
			: null;

		let newQuantity = parseInt(input.value, 10) + delta;

		if (!isNaN(max) && newQuantity > max) {
			if (warning) warning.classList.remove("visually-hidden");
			if (incBtn) incBtn.disabled = true;
			return;
		} else {
			if (warning) warning.classList.add('visually-hidden');
			if (incBtn) incBtn.disabled = false;
		}

		newQuantity = newQuantity < 1 ? 0 : newQuantity;
		input.value = newQuantity;

		const formData = new FormData(form);
		const bodyData = new URLSearchParams(formData);

		fetch("/cart/update.js", {
			method: "POST",
			body: bodyData,
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
		})
			.then((response) => response.json())
			.then(() => this.refreshCart())
			.catch((err) =>
				console.error("Error updating cart item quantity:", err)
			);
	}
}
